import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';

import { IUserInfo } from '@ruby/modules/account/models/contact.interface';
import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import * as fromCustomerByRole from '@ruby/modules/customer-select/store/reducers/role-customers.reducer';
import { selectAssignedCustomers } from '@ruby/modules/customer-select/store/selectors/offline-customers.selectors';
import { selectAssignedCustomersByRole } from '@ruby/modules/customer-select/store/selectors/role-customers.selectors';
import { hasRole } from '@ruby/modules/security/store/selectors/authorizations.selectors';
import { ThirdPartyAuthorizationEffects } from '@ruby/modules/third-party/store/effects/third-party-authorization.effects';
import { ThirdPartyCompaniesEffects } from '@ruby/modules/third-party/store/effects/third-party-companies.effects';
import { ThirdPartyCoreEffects } from '@ruby/modules/third-party/store/effects/third-party-core.effects';
import * as fromThirdPartyAuthorization from '@ruby/modules/third-party/store/reducers/third-party-authorization.reducer';
import * as fromThirdPartyCompanies from '@ruby/modules/third-party/store/reducers/third-party-companies.reducer';
import * as fromThirdPartyCore from '@ruby/modules/third-party/store/reducers/third-party-core.reducer';
import * as fromThirdPartyStepper from '@ruby/modules/third-party/store/reducers/third-party-stepper.reducer';
import { ERole } from '@ruby/shared/enums/roles.enums';
import { selectQueryParam } from '@ruby/store/selectors/root.selectors';
import { selectUserInfoState } from '@ruby/store/selectors/user-account.selectors';

export const featureKey = 'threePl';

export const selectThirdPartyState = createFeatureSelector<State>(featureKey);

export const selectThirdPartyCoreState = createSelector(
  selectThirdPartyState,
  (state: State) => state[fromThirdPartyCore.featureKey]
);

export const selectThirdPartyCompaniesState = createSelector(
  selectThirdPartyState,
  (state: State) => state[fromThirdPartyCompanies.featureKey]
);

export const selectThirdPartyStepperState = createSelector(
  selectThirdPartyState,
  (state: State) => state[fromThirdPartyStepper.featureKey]
);

export const selectThirdPartyCompaniesWithCoreState = createSelector(
  selectThirdPartyCoreState,
  selectThirdPartyCompaniesState,
  selectThirdPartyStepperState,
  (
    core: fromThirdPartyCore.State,
    companies: fromThirdPartyCompanies.State,
    stepper: fromThirdPartyStepper.State) => (
    {
      core,
      companies,
      stepper
    }
  )
);

export const selectThirdPartyAuthorizationsState = createSelector(
  selectThirdPartyState,
  (state: State) => state[fromThirdPartyAuthorization.featureKey]
);

export const selectThirdPartyAuthorizationWithCompaniesState = createSelector(
  selectThirdPartyAuthorizationsState,
  selectThirdPartyCompaniesState,
  (state: fromThirdPartyAuthorization.State, companies: fromThirdPartyCompanies.State) => ({ state, companies })
);

export const selectThirdPartyContractState = createSelector(
  selectThirdPartyCoreState,
  selectThirdPartyAuthorizationsState,
  selectThirdPartyCompaniesState,
  selectThirdPartyStepperState,
  selectQueryParam('contractId'),
  (
    core: fromThirdPartyCore.State,
    authorizations: fromThirdPartyAuthorization.State,
    companies: fromThirdPartyCompanies.State,
    stepper: fromThirdPartyStepper.State,
    contractId?: string
  ) => (
    {
      core,
      authorizations,
      companies,
      stepper,
      contractId
    }
  )
);

export const selectThirdPartyStepperWithCustomer = createSelector(
  selectThirdPartyCoreState,
  selectThirdPartyCompaniesState,
  selectThirdPartyStepperState,
  selectAssignedCustomers,
  (core: fromThirdPartyCore.State, companies: fromThirdPartyCompanies.State, stepper: fromThirdPartyStepper.State, customers?: Array<ICustomer>) => ({
    core,
    companies,
    stepper,
    customers: customers ? customers.filter(
      (value: ICustomer, index: number, arrayCustomers: Array<ICustomer>) =>
        arrayCustomers.findIndex(
          (customer: ICustomer) => (customer.customerName === value.customerName)) === index) : []
  })
);

export const selectThirdPartyCustomersByRole = createSelector(
  selectThirdPartyCoreState,
  selectThirdPartyAuthorizationsState,
  selectThirdPartyStepperState,
  selectAssignedCustomersByRole,
  (
    core: fromThirdPartyCore.State,
    authorizations: fromThirdPartyAuthorization.State,
    stepper: fromThirdPartyStepper.State,
    customersByRole?: fromCustomerByRole.State
  ) =>
    ({
      core,
      authorizations,
      stepper,
      customersByRole
    })
);

export const selectThirdPartyRole = createSelector(
  selectThirdPartyCoreState,
  hasRole(ERole.ROLE_3PL),
  (core: fromThirdPartyCore.State, has3PLRole?: boolean) => ({ core, has3PLRole })
);

export const selectThirdPartyPreviewState = createSelector(
  selectThirdPartyCoreState,
  selectThirdPartyCompaniesState,
  selectThirdPartyAuthorizationsState,
  selectThirdPartyStepperState,
  selectUserInfoState,
  (
    core: fromThirdPartyCore.State,
    companies: fromThirdPartyCompanies.State,
    authorizations: fromThirdPartyAuthorization.State,
    stepper: fromThirdPartyStepper.State,
    userInfo: IUserInfo) =>
    ({
      core,
      companies,
      authorizations,
      stepper,
      userInfo
    })
);

export interface State {
  [fromThirdPartyCore.featureKey]: fromThirdPartyCore.State;
  [fromThirdPartyCompanies.featureKey]: fromThirdPartyCompanies.State;
  [fromThirdPartyAuthorization.featureKey]: fromThirdPartyAuthorization.State;
  [fromThirdPartyStepper.featureKey]: fromThirdPartyStepper.State;
}

export const reducer: ActionReducerMap<State> = {
  [fromThirdPartyCore.featureKey]: fromThirdPartyCore.reducer,
  [fromThirdPartyCompanies.featureKey]: fromThirdPartyCompanies.reducer,
  [fromThirdPartyAuthorization.featureKey]: fromThirdPartyAuthorization.reducer,
  [fromThirdPartyStepper.featureKey]: fromThirdPartyStepper.reducer
};

export const effects = [
  ThirdPartyCoreEffects,
  ThirdPartyCompaniesEffects,
  ThirdPartyAuthorizationEffects
];
